import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Send } from 'lucide-react';
import Modal from '../ui/Modal.jsx';
import Avatar from '../ui/Avatar.jsx';
import Button from '../ui/Button';
import { Textarea } from '../ui/Input.jsx';
import { ButtonLoader } from '../ui/LoadingSpinner';
import triggerHaptic, { haptic } from '../../utils/haptics';
import useConnectRequest, { DEFAULT_CONNECT_MESSAGE } from '../../hooks/useConnectRequest';

/**
 * Vernetzen ohne Seitenwechsel (Okt 2026, „App wie Spot“).
 *
 * Bis dahin fuehrte „Vernetzen“ auf eine eigene Seite. Im Spot wechselt der
 * Knopf sofort auf „Anfrage gesendet ✓“; die Landingpage verspricht „gern mit
 * ein paar Worten dazu“. Beides zusammen: ein Blatt mit optionaler Nachricht,
 * danach steht man wieder auf der Karte, deren Knopf umspringt. Kein Toast —
 * der Knopf IST die Bestaetigung (CD-Toastregel).
 */
const ConnectSheet = ({ open, onClose, recipient, onSent, origin }) => {
  const navigate = useNavigate();
  const [message, setMessage] = useState('');
  const { send, isSending, error, setError } = useConnectRequest(origin);

  useEffect(() => {
    if (open) {
      setMessage('');
      setError('');
    }
  }, [open, setError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await send(recipient.id, message);
    if (result.ok) {
      triggerHaptic('success');
      onSent?.();
      onClose();
      return;
    }
    if (result.chatId) {
      onClose();
      navigate(`/chat/${result.chatId}`);
      return;
    }
    // Gescheitert: Die Meldung steht inline im Blatt (kein Toast), deshalb
    // kommt der Fehler-Impuls hier, genau einmal.
    haptic?.error?.();
  };

  return (
    <Modal open={open} onClose={onClose} title="Vernetzen" size="sm" bodyClassName="px-5 pb-5 pt-3">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex items-center gap-3">
          <Avatar name={recipient.name} src={recipient.image || undefined} size="lg" />
          <div className="min-w-0">
            <p className="truncate text-title-s font-semibold text-foreground">{recipient.name}</p>
            <p className="text-meta text-muted-foreground">Wird benachrichtigt und kann annehmen</p>
          </div>
        </div>
        <div>
          <label htmlFor="connect-message" className="mb-1.5 block text-sm font-medium text-foreground">
            Ein paar Worte dazu <span className="font-normal text-muted-foreground">(optional)</span>
          </label>
          <Textarea
            id="connect-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={DEFAULT_CONNECT_MESSAGE}
            rows={3}
            maxLength={500}
            enterKeyHint="send"
          />
        </div>
        {error && (
          <p role="alert" className="rounded-xl border border-danger/30 bg-danger-soft p-3 text-sm text-foreground">
            {error}
          </p>
        )}
        <Button type="submit" fullWidth disabled={isSending}>
          {isSending ? <ButtonLoader /> : <Send className="h-4 w-4" aria-hidden="true" />}
          Anfrage senden
        </Button>
      </form>
    </Modal>
  );
};

ConnectSheet.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  recipient: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    image: PropTypes.string,
  }).isRequired,
  onSent: PropTypes.func,
  /** Herkunft fuer den Forschungsexport, siehe useConnectRequest */
  origin: PropTypes.oneOf(['ROUND', 'SEARCH', 'SKILL_BROWSE', 'PROFILE_LINK', 'OTHER']),
};

export default ConnectSheet;
